import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import AuthContext from '../contexts/AuthContext';
import {
  UserRegistrationChart,
  SubmissionsChart,
  TestCategoriesChart,
  PerformanceDistributionChart,
  TopTestsPerformanceChart,
  UserTypesChart
} from '../components/DashboardCharts';

const AdminDashboard = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    users: [],
    tests: [],
    submissions: [],
    stats: {},
    trends: {
      userRegistration: [],
      submissions: []
    }
  });
  const [timeRange, setTimeRange] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.isAdmin) {
      navigate('/test-list');
      return;
    }
    
    fetchDashboardData();
  }, [user, authLoading]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardResponse, usersResponse] = await Promise.all([
        api.get('/admin/dashboard', { params: { timeRange } }),
        api.get('/admin/users')
      ]);

      const dashboardData = dashboardResponse.data;
      const users = usersResponse.data;

      // Combine data and calculate additional stats
        setDashboardData({
          users,
          tests: dashboardData.mostAttemptedTests,
          submissions: [
            ...dashboardData.bestPerformers,
            ...dashboardData.minErrorUsers
          ],
          stats: {
            totalUsers: dashboardData.overview.totalUsers,
            totalTests: dashboardData.overview.totalTests,
            totalAttempts: dashboardData.overview.totalAttempts,
            premiumUsers: dashboardData.overview.premiumUsers,
            premiumGrowthLastMonth: dashboardData.overview.premiumGrowthLastMonth,
            activeUsers: dashboardData.overview.uniqueUserAttempts,
            bestPerformers: dashboardData.bestPerformers,
            minErrorUsers: dashboardData.minErrorUsers,
            mostAttemptedTests: dashboardData.mostAttemptedTests
          },
          trends: dashboardData.trends
        });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 90) return '#28a745';
    if (accuracy >= 70) return '#ffc107';
    return '#dc3545';
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  if (authLoading || loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        flexDirection: 'column'
      }}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        background: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '12px',
        color: '#e53e3e',
        margin: '2rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>Error Loading Dashboard</h3>
        <p>{error}</p>
        <button
          onClick={fetchDashboardData}
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h1 style={{
            fontSize: '2rem',
            color: '#2c3e50',
            margin: 0
          }}>Admin Dashboard</h1>
          <a
            href="/admin/user-info"
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              transition: 'background-color 0.2s ease'
            }}
          >
            User Info Analysis
          </a>
        </div>
        <div style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center'
        }}>
          <label htmlFor="timeRange" style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            fontWeight: '500'
          }}>
            Time Range:
          </label>
          <select
            id="timeRange"
            value={timeRange}
            onChange={(e) => {
              setTimeRange(e.target.value);
              fetchDashboardData();
            }}
            style={{
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
              fontSize: '0.875rem',
              cursor: 'pointer',
              backgroundColor: '#ffffff'
            }}
          >
            <option value="all">All Time</option>
            <option value="30d">Last 30 Days</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button
            onClick={fetchDashboardData}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            margin: '0 0 0.5rem 0',
            fontWeight: '500'
          }}>Total Users</h3>
          <p style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#2c3e50',
            margin: 0
          }}>{formatNumber(dashboardData.stats.totalUsers)}</p>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            margin: '0 0 0.5rem 0',
            fontWeight: '500'
          }}>Total Tests</h3>
          <p style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#2c3e50',
            margin: 0
          }}>{formatNumber(dashboardData.stats.totalTests)}</p>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            margin: '0 0 0.5rem 0',
            fontWeight: '500'
          }}>Total Attempts</h3>
          <p style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#2c3e50',
            margin: 0
          }}>{formatNumber(dashboardData.stats.totalAttempts)}</p>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            margin: '0 0 0.5rem 0',
            fontWeight: '500'
          }}>Premium Users</h3>
          <p style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#8e44ad',
            margin: 0
          }}>{formatNumber(dashboardData.stats.premiumUsers)}</p>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            margin: '0 0 0.5rem 0',
            fontWeight: '500'
          }}>New Premium Last Month</h3>
          <p style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#28a745',
            margin: 0
          }}>{formatNumber(dashboardData.stats.premiumGrowthLastMonth)}</p>
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            color: '#6c757d',
            margin: '0 0 0.5rem 0',
            fontWeight: '500'
          }}>Active Users</h3>
          <p style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#28a745',
            margin: 0
          }}>{formatNumber(dashboardData.stats.activeUsers)}</p>
        </div>
      </div>

      {/* Charts Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <UserRegistrationChart data={dashboardData.trends.userRegistration} />
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <SubmissionsChart data={dashboardData.trends.submissions} />
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <UserTypesChart 
            totalUsers={dashboardData.stats.totalUsers}
            premiumUsers={dashboardData.stats.premiumUsers}
          />
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <PerformanceDistributionChart submissions={dashboardData.submissions} />
        </div>

        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          gridColumn: '1 / -1' // Span full width
        }}>
          <TopTestsPerformanceChart tests={dashboardData.tests} />
        </div>
      </div>

      {/* Test Categories Chart - Full Width */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginBottom: '2rem'
      }}>
        <TestCategoriesChart tests={dashboardData.tests} />
      </div>

      {/* Best Performers */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          color: '#2c3e50',
          margin: '0 0 1rem 0'
        }}>Best Performers</h2>
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>User</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Accuracy</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>WPM</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.stats.bestPerformers?.map((submission, index) => {
                const userData = dashboardData.users.find(u => u.id === submission.userId);
                return (
                  <tr key={index}>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {submission.fullName || userData?.fullName || 'Unknown User'}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef',
                      fontWeight: '600',
                      color: getAccuracyColor(submission.accuracy)
                    }}>
                      {submission.accuracy}%
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {submission.wpm}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {new Date(submission.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Users with Minimum Errors */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          color: '#2c3e50',
          margin: '0 0 1rem 0'
        }}>Users with Minimum Errors</h2>
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>User</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Errors</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Accuracy</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.stats.minErrorUsers?.map((submission, index) => {
                const userData = dashboardData.users.find(u => u.id === submission.userId);
                return (
                  <tr key={index}>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {submission.fullName || userData?.fullName || 'Unknown User'}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef',
                      fontWeight: '600',
                      color: '#28a745'
                    }}>
                      {submission.errors}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef',
                      fontWeight: '600',
                      color: getAccuracyColor(submission.accuracy)
                    }}>
                      {submission.accuracy}%
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {new Date(submission.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Most Attempted Tests */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          color: '#2c3e50',
          margin: '0 0 1rem 0'
        }}>Most Attempted Tests</h2>
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Test Name</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Category</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Attempts</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #e9ecef',
                  fontSize: '0.875rem',
                  color: '#6c757d'
                }}>Avg Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.stats.mostAttemptedTests?.map((test, index) => {
                return (
                  <tr key={index}>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {test.name}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      <span style={{
                        background: '#46923c',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        textTransform: 'uppercase'
                      }}>
                        {test.category}
                      </span>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef',
                      fontWeight: '600',
                      color: '#2c3e50'
                    }}>
                      {formatNumber(test.attempts)}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #e9ecef',
                      fontWeight: '600',
                      color: getAccuracyColor(test.avgAccuracy)
                    }}>
                      {test.avgAccuracy}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add CSS animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminDashboard;
